import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { keepPreviousData, useQuery } from '@tanstack/react-query';
import type { OrderListRow } from '@order-pilot/shared';
import { AsyncSection } from '@/components/AsyncSection';
import { Button } from '@/components/Button';
import { IconChevronLeft, IconChevronRight, IconReceipt } from '@/components/Icons';
import { PageHeader } from '@/components/PageHeader';
import {
  ReportColumnsButton,
  ReportFiltersButton,
  ReportSearchField,
  ReportToolbar,
} from '@/components/ReportToolbar';
import { applyReportLayout, DataTable, TableSkeleton } from '@/components/Table';
import type { Column } from '@/components/Table';
import { usePortal } from '@/features/portal/PortalContext';
import { OrderStatusBadge, PaymentStatusBadge } from '@/features/portal/badges';
import type { OrderListParams } from '@/features/portal/api';
import { fetchOrders, portalKeys } from '@/features/portal/api';
import { toReportRange } from '@/features/portal/filters';
import { parseFilterRows, serialiseFilters } from '@/features/reports/reportFilters';
import { useReportLayout } from '@/features/reports/useReportLayout';
import { formatCount, formatMoney, formatPosDate, formatPosTime } from '@/lib/format';

const PAGE_SIZE = 20;

const FILTER_FIELDS = ['Order #', 'Customer', 'Status'] as const;

const TOGGLEABLE = ['branch', 'customer', 'orderType', 'paymentMethod', 'status', 'paymentStatus', 'total', 'cashier'] as const;

const COLUMN_LABELS: Record<string, string> = {
  branch: 'Branch',
  customer: 'Customer',
  orderType: 'Type',
  paymentMethod: 'Payment',
  status: 'Order Status',
  paymentStatus: 'Payment Status',
  total: 'Total',
  cashier: 'By',
};

/**
 * Orders is server-paginated (25 rows at a time), so unlike the other six
 * report pages the toolbar's search and Filters go into the API query — see
 * `apps/api/src/portal/orders.service.ts` — rather than being applied to an
 * already-fetched list with `applyClientFilters`.
 */
export function OrdersPage() {
  const { currency, filters, multiBranch } = usePortal();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();
  const layout = useReportLayout('orders', TOGGLEABLE);

  const query = searchParams.get('q') ?? '';
  const pageParam = Number.parseInt(searchParams.get('page') ?? '1', 10);
  const page = Number.isFinite(pageParam) && pageParam > 0 ? pageParam : 1;
  const filterRows = useMemo(() => parseFilterRows(searchParams.get('filters')), [searchParams]);
  const matchAny = searchParams.get('matchAny') === 'true';

  const [searchInput, setSearchInput] = useState(query);

  const patchParams = useCallback(
    (changes: Record<string, string | null>) => {
      const next = new URLSearchParams(searchParams);
      for (const [key, value] of Object.entries(changes)) {
        if (value === null || value === '') next.delete(key);
        else next.set(key, value);
      }
      setSearchParams(next, { replace: true });
    },
    [searchParams, setSearchParams],
  );

  // Debounce typing before it becomes a query key — and a URL the user can share.
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const trimmed = searchInput.trim();
      if (trimmed !== query) patchParams({ q: trimmed, page: null });
    }, 300);
    return () => window.clearTimeout(timer);
  }, [searchInput, query, patchParams]);

  const applyFilters = useCallback(
    (rows: typeof filterRows, nextMatchAny: boolean) => {
      patchParams({
        filters: serialiseFilters(rows) ?? null,
        matchAny: nextMatchAny ? 'true' : null,
        page: null,
      });
    },
    [patchParams],
  );

  const params = useMemo<OrderListParams>(
    () => ({
      ...toReportRange(filters.filters),
      page,
      pageSize: PAGE_SIZE,
      q: query,
      filters: serialiseFilters(filterRows),
      matchAny: matchAny ? 'true' : undefined,
    }),
    [filters.filters, page, query, filterRows, matchAny],
  );

  const ordersQuery = useQuery({
    queryKey: portalKeys.orders(params),
    queryFn: ({ signal }) => fetchOrders(params, signal),
    placeholderData: keepPreviousData,
  });

  const total = ordersQuery.data?.total ?? 0;
  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));
  const rangeStart = total === 0 ? 0 : (page - 1) * PAGE_SIZE + 1;
  const rangeEnd = Math.min(page * PAGE_SIZE, total);
  // Returning from a detail view must land back on the same filtered page.
  const listSearch = searchParams.toString();

  // Column for column with the POS's own Order History, in its order:
  // Order · Customer · Type · Payment · Order Status · Payment Status · Total · By
  //
  // Nothing is hidden by viewport any more. Type, Status and the operator name
  // used to drop out below md/lg, so on an ordinary laptop the table quietly
  // showed fewer fields than the till did — which reads as missing data, not as
  // a layout choice. The table scrolls sideways instead (TableScroll already
  // wraps it, same as the Sales report). Display options now lets the user
  // hide/reorder the non-pinned ones instead.
  const baseColumns = useMemo<Array<Column<OrderListRow>>>(() => {
    const list: Array<Column<OrderListRow>> = [
      {
        key: 'orderNumber',
        header: 'Order',
        pinned: true,
        cell: (row) => (
          <div>
            <Link
              to={{ pathname: `/orders/${encodeURIComponent(row.id)}`, search: listSearch }}
              className="rounded font-medium text-slate-900 hover:text-brand-800 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600 focus-visible:ring-offset-2"
            >
              {row.orderNumber}
            </Link>
            <p className="mt-0.5 whitespace-nowrap text-xs text-slate-400">
              {formatPosDate(row.date)} · {formatPosTime(row.time)}
            </p>
          </div>
        ),
      },
      {
        key: 'branch',
        header: 'Branch',
        cell: (row) => <span className="text-slate-500">{row.branchName || '—'}</span>,
      },
      {
        key: 'customer',
        header: 'Customer',
        cell: (row) => <span className="text-slate-700">{row.customerName || 'Walk-in'}</span>,
      },
      {
        key: 'orderType',
        header: 'Type',
        cell: (row) => <span className="text-slate-500">{row.orderType || '—'}</span>,
      },
      {
        key: 'paymentMethod',
        header: 'Payment',
        cell: (row) => <span className="text-slate-500">{row.paymentMethod || '—'}</span>,
      },
      {
        key: 'status',
        header: 'Order Status',
        cell: (row) => <OrderStatusBadge status={row.status} />,
      },
      {
        key: 'paymentStatus',
        header: 'Payment Status',
        cell: (row) => <PaymentStatusBadge status={row.paymentStatus} />,
      },
      {
        key: 'total',
        header: 'Total',
        align: 'right',
        cellClassName: 'tabular-nums font-medium text-slate-900',
        cell: (row) => formatMoney(row.total, currency),
      },
      {
        key: 'cashier',
        // "By" is what the till calls it, and it is `created_by` on both sides —
        // not necessarily a cashier, so the POS's neutral label is the right one.
        header: 'By',
        cell: (row) => <span className="text-slate-500">{row.cashier || '—'}</span>,
      },
      {
        key: 'open',
        header: 'Open order',
        headerHidden: true,
        pinned: true,
        align: 'right',
        cell: (row) => (
          <Link
            to={{ pathname: `/orders/${encodeURIComponent(row.id)}`, search: listSearch }}
            aria-label={`Open order ${row.orderNumber}`}
            className="inline-flex rounded p-1 text-slate-300 hover:text-brand-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-brand-600"
          >
            <IconChevronRight className="h-4 w-4" />
          </Link>
        ),
      },
    ];
    return list;
  }, [currency, listSearch]);

  const columns = useMemo(() => {
    const resolved = applyReportLayout(baseColumns, layout);
    // A single-branch tenant only ever sees one value repeated — data-driven,
    // not viewport-driven.
    return multiBranch ? resolved : resolved.filter((column) => column.key !== 'branch');
  }, [baseColumns, layout, multiBranch]);

  return (
    <>
      <PageHeader
        title="Orders"
        description="Every order in the range. Open one to see its items and totals."
      />

      <div className="card overflow-hidden">
        <div className="flex flex-wrap items-center justify-between gap-3 border-b border-slate-200 px-4 py-3.5 sm:px-5">
          <ReportToolbar>
            <ReportSearchField
              value={searchInput}
              onChange={setSearchInput}
              placeholder="Order number or customer…"
            />
            <ReportFiltersButton
              fields={FILTER_FIELDS}
              applied={filterRows}
              matchAny={matchAny}
              onApply={applyFilters}
              placeholderFor={(field) => (field === 'Status' ? 'Completed / Cancelled' : 'value')}
            />
            <ReportColumnsButton layout={layout} labels={COLUMN_LABELS} />
          </ReportToolbar>
          <p className="text-xs text-slate-500" aria-live="polite">
            {ordersQuery.isPending
              ? 'Loading…'
              : `${formatCount(total)} order${total === 1 ? '' : 's'}`}
            {!ordersQuery.isPending && ordersQuery.isFetching ? ' · refreshing' : ''}
          </p>
        </div>

        <AsyncSection
          state={ordersQuery}
          skeleton={<TableSkeleton rows={8} columns={6} />}
          isEmpty={(data) => data.items.length === 0}
          emptyIcon={<IconReceipt className="h-5 w-5" />}
          emptyTitle={query ? `No orders match “${query}”` : 'No orders in this range'}
          emptyDescription={
            query
              ? 'Try a different order number or customer name, or clear the search.'
              : 'Pick a wider date range, or check another branch.'
          }
        >
          {(data) => (
            <DataTable
              caption="Orders with customer, type, payment, status and total"
              columns={columns}
              rows={data.items}
              rowKey={(row) => row.id}
              layout={layout}
              // Mouse convenience only — the order number in each row is still a
              // real link, so keyboard, screen readers and open-in-new-tab keep
              // working. See onRowClick in DataTable for what it declines to
              // treat as a click.
              onRowClick={(row) =>
                navigate({
                  pathname: `/orders/${encodeURIComponent(row.id)}`,
                  search: listSearch,
                })
              }
            />
          )}
        </AsyncSection>

        {!ordersQuery.isPending && !ordersQuery.isError && total > 0 ? (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-200 px-4 py-3.5 sm:px-5">
            <p className="text-xs tabular-nums text-slate-500">
              Showing {rangeStart}–{rangeEnd} of {formatCount(total)}
            </p>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                icon={<IconChevronLeft className="h-3.5 w-3.5" />}
                disabled={page <= 1 || ordersQuery.isFetching}
                onClick={() => patchParams({ page: String(Math.max(1, page - 1)) })}
              >
                Previous
              </Button>
              <span className="px-1 text-xs tabular-nums text-slate-500">
                Page {page} of {totalPages}
              </span>
              <Button
                size="sm"
                disabled={page >= totalPages || ordersQuery.isFetching}
                onClick={() => patchParams({ page: String(page + 1) })}
              >
                Next
                <IconChevronRight className="h-3.5 w-3.5" />
              </Button>
            </div>
          </div>
        ) : null}
      </div>
    </>
  );
}
